/**
 * TRT product identification — the single source of truth for "is this a
 * testosterone (TRT) order?" on the customer-facing pages.
 *
 * ⚠️ Matching is EXACT, never a substring. `Depo-Testosterone`
 * (`depo-testosterone`) is a real, active product that is NOT part of Project
 * Testosterone, so a substring match on "testosterone" sweeps it in and shows it
 * the DoctorNow video-consult copy it must never receive. Measured 2026-08-14:
 * three active products match /testosterone/i, and only two of them are ours.
 *
 * ⚠️ The slug is NOT always present. `checkoutController.submitCheckout` returns
 * `product: { id, name }` with no slug, while the order-list transformer does
 * carry one — so this accepts either and falls back to an exact, case-insensitive
 * name match. Slug wins when present, because names are editable in the admin
 * product editor and slugs are not.
 *
 * The canonical pair is `api/scripts/refreshDoctorNowTrtMappings.js` TRT_PRODUCTS.
 */

const TRT_PRODUCT_SLUGS = Object.freeze(['testosterone-cream', 'testosterone-cypionate']);

const TRT_PRODUCT_NAMES = Object.freeze(['testosterone cream', 'testosterone cypionate']);

const normalize = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

/**
 * True when the given identifiers name a Project Testosterone product.
 * Total function — null / undefined / garbage returns false.
 */
const isTrtProduct = (product) => {
  const slug = normalize(product?.slug);
  if (slug) return TRT_PRODUCT_SLUGS.includes(slug);
  return TRT_PRODUCT_NAMES.includes(normalize(product?.name));
};

/**
 * True when an order-list row is a TRT order. Reads the first item, which is the
 * shape `OrdersPage` already uses to resolve a product slug.
 */
const isTrtOrder = (order) => {
  const item = order?.items?.[0];
  return isTrtProduct({
    slug: item?.product?.slug ?? item?.productSlug,
    name: item?.product?.name ?? item?.productName
  });
};

export { TRT_PRODUCT_SLUGS, TRT_PRODUCT_NAMES, isTrtProduct, isTrtOrder };
